<svelte:options customElement="dst-readlater" />

<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { signIn, userIsLoggedIn } from '$lib/authentication';
	import languageStrings from '$i18n/de';

	export let checked: boolean = false;
	export let hasLabel: boolean = true;
	export let isLoggedIn: boolean = false;
	export let url: string;
	export let title: string;

	$: ariaLabel = checked ? languageStrings.readlater.ariaLabel.remove : languageStrings.readlater.ariaLabel.add;
	$: label = checked ? languageStrings.readlater.label.remove : languageStrings.readlater.label.add;

	onMount(async () => {
		window.addEventListener('message', onMessage);
		isLoggedIn = await userIsLoggedIn();
		checked = getInitialState();
	});

	onDestroy(() => {
		window.removeEventListener('message', onMessage);
	});

	const getInitialState = () => {
		const storedList = JSON.parse(sessionStorage.getItem('readinglist'));
		if (!storedList || !Array.isArray(storedList)) {
			return false;
		}

		return storedList.some((item: ListItem) => item.url === url);
	};

	const handleClick = async (e: MouseEvent): Promise<void> => {
		if (!(await userIsLoggedIn())) {
			e.preventDefault();
			return await signIn();
		}

		checked = !checked;

		const event = {
			action: checked ? 'add' : 'remove',
			item: {
				url,
				title,
			},
		};

		postMessage(event);
	};

	/**
	 * Method that handles the update event sent by the readinglist
	 * changes the state of the component if its url is in the update array
	 * @param {MessageEvent} event - event sent by readinglist
	 */
	const onMessage = async (event: MessageEvent) => {
		if (event.data.action !== 'update' && event.data.eventName !== 'dst.auth.stateChanged') {
			return;
		}

		if (event.data.action === 'update') {
			checked = event.data.items?.some((item: ListItem) => item.url === url) ? true : false;
		}

		// This message handler revalidates the login-state on state change
		if (event.data.eventName === 'dst.auth.stateChanged') {
			isLoggedIn = await userIsLoggedIn();
			checked = false;
		}
	};
</script>

<input type="checkbox" id="checkbox" disabled={!url || !title} checked={checked} on:click={handleClick} aria-label={ariaLabel} />
<label for="checkbox" aria-hidden="true" part="button">
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" class:filled={checked}>
		{#if !isLoggedIn || !checked}
			<path class="path-list" d="M17,17.5L12,14.5L7,17.5V5H17M17,3H7A2, 2 0 0,0 5,5V21L12,17L19,21V5C19,3.89 18.1,3 17,3Z" fill="currentColor" />
		{:else}
			<path class="path-list" d="M17,3H7A2, 2 0 0,0 5,5V21L12,17L19,21V5C19,3.89 18.1,3 17,3Z" fill="currentColor" />
		{/if}
	</svg>

	{#if hasLabel}
		<span>{label}</span>
	{/if}
</label>

<style lang="scss">
	@use '@derstandard/styling-toolkit/src/a11y/focusstyles';
	@use '@derstandard/styling-toolkit/src/a11y/screenreader';

	:host {
		--path-list-opacity: var(--contrast-opacity-100, 0.7);
		display: inline-block;
	}

	.path-list {
		opacity: var(--path-list-opacity);
		transition: opacity 0.2s;
	}

	input {
		@extend .sr-only;

		&:focus-visible + label {
			--path-list-opacity: 1;
			background: hsl(var(--theme--on-surface--hsl) / var(--opacity-15));
			outline-offset: 2px;
			outline: 3px solid var(--theme--on-base);
			text-decoration: none;
		}

		&:checked + label {
			background: var(--theme--highlight);
			color: var(--theme--on-highlight);
		}

		&:disabled + label {
			background: hsl(var(--theme--on-surface--hsl) / var(--opacity-5));
			border-color: hsl(var(--theme--on-surface--hsl) / var(--contrast-opacity-0, var(--opacity-0)));
			color: hsl(var(--theme--on-surface--hsl) / var(--contrast-opacity-50, var(--opacity-30)));
			cursor: default;
		}
	}

	label {
		align-items: center;
		background: hsl(var(--theme--on-surface--hsl) / 0.07);
		border-radius: 3px;
		border: 2px solid hsl(var(--theme--on-surface--hsl) / var(--contrast-opacity-100, var(--opacity-0)));
		box-sizing: border-box;
		color: var(--theme--on-surface);
		cursor: pointer;
		display: inline-flex;
		font-family: var(--font-info);
		font-size: 14px;
		font-weight: 500;
		gap: 10px;
		justify-content: center;
		line-height: 1.1;
		margin: 0;
		min-height: 32px;
		min-width: 120px;
		padding: 5px 12px;
		position: relative;
		text-align: center;
		text-decoration: none;
		touch-action: manipulation;
		transition: background-color 0.1s ease;
		vertical-align: middle;

		&:hover {
			--path-list-opacity: var(--contrast-opacity-100, 1);
			background: hsl(var(--theme--on-surface--hsl) / var(--opacity-10));
		}
	}

	svg {
		flex-shrink: 0;
		height: 16px;
		margin: 0 -6px;
		width: 16px;


		&.filled .path-list {
			animation: fillAnimation 0.4s 0.2s;
			opacity: 1;
			transform-origin: center;
		}

		// NOTE: iOS < 14 doesn't support flex-gap
		@supports (-webkit-touch-callout: none) and (not (translate: none)) {
			margin: 0;
		}
	}

	@keyframes fillAnimation {
		50% {
			transform: scale(1.4);
		}
	}

	// NOTE: tmp for onboarding
	@keyframes -global-highlightAnimation {
		20% {
			transform: translatex(-50%) translatey(-50%) scale(1);
		}
		50% {
			transform: translatex(-50%) translatey(-50%) scale(1.1);
		}
		80% {
			transform: translatex(-50%) translatey(-50%) scale(1);
		}
	}

	// Button without Background and Label
	:host(:not(.article-readlater)) {
		--path-list-opacity: var(--contrast-opacity-100, 0.5);

		label {
			background-color: transparent;
			border: none;
			color: currentcolor;
			min-height: 42px;
			min-width: unset;
			padding: 8px 21px;

			&:hover {
				--path-list-opacity: var(--contrast-opacity-100, 0.8);
			}
		}

		input:checked + label .path-list {
			fill: var(--theme--highlight);
		}

		span {
			display: none;
		}

		svg {
			flex-shrink: 0;
			height: 18px;
			margin: 0 -12px;
			width: 18px;
		}
	}
</style>
